import React, { useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { AuthContainer } from "components/Layout/AuthContainer";
import { StyledFormicError } from "modals/ResetPasswordModal/styles";
import {
  getValidationSchema,
  initialValues,
  ROLE_OPTIONS,
  SearchMode,
} from "./config";
import {
  AccountRecoveryError,
  requestAccountRecovery,
} from "services/accountRecovery";
import { isAxiosError } from "axios";
import { GuideLink } from "pages/AuthGuides/GuideLink";
import { AdminRoutes } from "config/routes";

const AccountRecovery: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{
    name: string;
    email: string;
  } | null>(null);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: getValidationSchema(),
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      setSuccess(null);
      try {
        const result =
          values.searchMode === "email"
            ? await requestAccountRecovery({ email: values.email })
            : await requestAccountRecovery({
                firstName: values.firstName,
                lastName: values.lastName,
                role: values.role as "director" | "coach" | "student",
              });
        setSuccess({ name: result.name, email: result.email });
      } catch (err) {
        if (isAxiosError<AccountRecoveryError>(err)) {
          const data = err.response?.data;
          const apiMessage =
            typeof data === "object" && data?.message
              ? data.message
              : typeof data === "string"
                ? data
                : null;
          setError(
            apiMessage ||
              err.message ||
              "Something went wrong. Please try again later.",
          );
        } else {
          setError("Something went wrong. Please try again later.");
        }
      } finally {
        setLoading(false);
      }
    },
  });

  const handleSearchModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: SearchMode | null,
  ) => {
    if (!newMode) return;
    formik.setFieldValue("searchMode", newMode);
    formik.setErrors({});
    setError(null);
  };

  const handleRoleSelect = (role: (typeof ROLE_OPTIONS)[number]["value"]) => {
    formik.setFieldValue("role", role);
    setError(null);
  };

  return (
    <AuthContainer loading={loading} loadingText="Looking up your account...">
      <Box sx={{ width: "100%", maxWidth: 480 }}>
        <Typography component="h1" variant="h6">
          Find My Account
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Search by email or by your name and role. We&apos;ll check if you have
          a VetNebula account and send instructions to create your password.
        </Typography>

        {success ? (
          <Box sx={{ mt: 3 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              We found your account, {success.name}!
            </Alert>
            <Typography variant="body2" sx={{ mb: 2 }}>
              We&apos;ve sent an email to <strong>{success.email}</strong> with a
              temporary password and a link to sign in. You&apos;ll be asked to
              create your own password right after signing in.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please check your inbox and spam folder. The message should arrive
              within a few minutes.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => navigate("/")}
            >
              Go to Sign In
            </Button>
          </Box>
        ) : (
          <FormikProvider value={formik}>
            <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 2 }}>
              <ToggleButtonGroup
                exclusive
                fullWidth
                value={formik.values.searchMode}
                onChange={handleSearchModeChange}
                sx={{ mb: 2 }}
              >
                <ToggleButton value="email">By Email</ToggleButton>
                <ToggleButton value="name">By Name</ToggleButton>
              </ToggleButtonGroup>

              {formik.values.searchMode === "email" ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    If you already have a VetNebula account with the same email
                    (for example, as a student or director), use the coach alias
                    you were invited with instead — such as{" "}
                    <strong>john.doe+coach@bluepearlvet.com</strong>.
                  </Typography>
                  <TextField
                    variant="standard"
                    onChange={formik.handleChange}
                    value={formik.values.email}
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    autoFocus
                  />
                  <ErrorMessage name="email" component={StyledFormicError} />
                </>
              ) : (
                <>
                  <TextField
                    variant="standard"
                    onChange={formik.handleChange}
                    value={formik.values.firstName}
                    margin="normal"
                    required
                    fullWidth
                    id="firstName"
                    label="First Name"
                    name="firstName"
                    autoComplete="given-name"
                    autoFocus
                  />
                  <ErrorMessage name="firstName" component={StyledFormicError} />

                  <TextField
                    variant="standard"
                    onChange={formik.handleChange}
                    value={formik.values.lastName}
                    margin="normal"
                    required
                    fullWidth
                    id="lastName"
                    label="Last Name"
                    name="lastName"
                    autoComplete="family-name"
                  />
                  <ErrorMessage name="lastName" component={StyledFormicError} />

                  <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                    Role
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {ROLE_OPTIONS.map((option) => (
                      <Button
                        key={option.value}
                        type="button"
                        fullWidth
                        variant={
                          formik.values.role === option.value
                            ? "contained"
                            : "outlined"
                        }
                        color="primary"
                        onClick={() => handleRoleSelect(option.value)}
                      >
                        {option.label}
                      </Button>
                    ))}
                  </Box>
                  <ErrorMessage name="role" component={StyledFormicError} />
                </>
              )}

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
              <Button
                disabled={loading}
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                sx={{ mt: 3, mb: 2 }}
              >
                Find My Account
              </Button>
            </Box>
          </FormikProvider>
        )}

        <GuideLink
          to={AdminRoutes.FindAccountGuide}
          label="View step-by-step guide"
        />
        <Button
          disabled={loading}
          fullWidth
          variant="text"
          color="primary"
          sx={{ mt: 1 }}
          onClick={() => navigate("/")}
        >
          Back to Sign In
        </Button>
      </Box>
    </AuthContainer>
  );
};

export default AccountRecovery;
